import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ReportGenUser } from "../../../shared/interfaces/User";
import {
  assignableStationRoles,
  getStationRoleLabel,
  getStationRoleRank,
} from "../../../shared/constants/roleConstants";
import {
  AllRolesReference,
  RoleCapabilityList,
} from "../../../shared/components/RoleReference";
import { ConfirmDialog } from "../../../shared/ui";

const STATION_ADMIN_RANK = getStationRoleRank("station_admin");

export interface EditRoleDialogProps {
  open: boolean;
  user: ReportGenUser | null;
  /** The caller's own role slug — decides which roles the picker offers. */
  callerRoleSlug: string | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (roleSlug: string) => void;
}

const EditRoleDialog: React.FC<EditRoleDialogProps> = ({
  open,
  user,
  callerRoleSlug,
  isSaving,
  onClose,
  onSave,
}) => {
  const [roleSlug, setRoleSlug] = useState("");
  const [showAllRoles, setShowAllRoles] = useState(false);

  const options = assignableStationRoles(callerRoleSlug);

  // Re-seed from the row each time the dialog opens for a user. The current
  // role may not be assignable by this caller (e.g. it defaulted from an
  // unranked slug) — only preselect it when it's actually offered.
  useEffect(() => {
    if (open) {
      const current = user?.role ?? "";
      setRoleSlug(options.some((role) => role.slug === current) ? current : "");
      setShowAllRoles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const isChanged = roleSlug !== "" && roleSlug !== (user?.role ?? "");
  const selected = options.find((role) => role.slug === roleSlug);

  // Admin membership is the consequential edge either way — call it out
  // before the save, not after.
  const currentRank = getStationRoleRank(user?.role);
  const grantsAdmin =
    isChanged &&
    selected !== undefined &&
    selected.rank >= STATION_ADMIN_RANK &&
    currentRank < STATION_ADMIN_RANK;
  const revokesAdmin =
    isChanged &&
    selected !== undefined &&
    selected.rank < STATION_ADMIN_RANK &&
    currentRank >= STATION_ADMIN_RANK;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={() => {
        if (isChanged && !isSaving) {
          onSave(roleSlug);
        }
      }}
      icon={<ManageAccountsOutlinedIcon />}
      iconVariant="tinted"
      title="Change Role"
      description={
        user
          ? `Update the role for ${[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email}. Current role: ${getStationRoleLabel(user.role) ?? "None"}.`
          : undefined
      }
      confirmLabel="Save"
      loading={isSaving}
      confirmDisabled={!isChanged}
      maxWidth={480}
    >
      <TextField
        select
        fullWidth
        label="Role"
        value={roleSlug}
        onChange={(e) => setRoleSlug(e.target.value)}
        slotProps={{
          select: {
            // Two-line menu items would otherwise render whole in the
            // closed input — collapse the value to the bare label.
            renderValue: () => selected?.label ?? "",
          },
        }}
        sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      >
        {options.map((role) => (
          <MenuItem key={role.slug} value={role.slug}>
            <Box sx={{ py: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {role.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", whiteSpace: "normal" }}
              >
                {role.description}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      {selected && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.75,
            borderRadius: 2,
            bgcolor: "action.hover",
            textAlign: "left",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              display: "block",
              mb: 0.75,
            }}
          >
            What a {selected.label} can do
          </Typography>
          <RoleCapabilityList capabilities={selected.capabilities} />
        </Box>
      )}

      {grantsAdmin && (
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2, textAlign: "left" }}>
          Station Admins have full station management access, including
          changing anyone's role — even other admins'.
        </Alert>
      )}
      {revokesAdmin && (
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2, textAlign: "left" }}>
          This removes their station management access immediately.
        </Alert>
      )}

      <Box sx={{ mt: 1, textAlign: "left" }}>
        <Button
          size="small"
          onClick={() => setShowAllRoles((prev) => !prev)}
          endIcon={showAllRoles ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: "none", fontWeight: 600, px: 0.5 }}
        >
          Compare all roles
        </Button>
        <Collapse in={showAllRoles}>
          <Box
            sx={{
              mt: 1,
              p: 1.75,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            <AllRolesReference />
          </Box>
        </Collapse>
      </Box>
    </ConfirmDialog>
  );
};

export default EditRoleDialog;
